import { INPUT_ACTION_COUNT, InputAction } from '../constants';
import { clamp } from '../math/scalar';

/** キー入力を DOM のフォーム/ボタンへ譲る判定。runtime 層のデバッグキーも同じ規則で動く。 */
export function isNativeUiControl(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.closest('button, input, select, textarea, a, [role="button"], [contenteditable="true"]') !== null;
}

const KEY_ACTION: Readonly<Record<string, InputAction>> = Object.freeze({
  ArrowUp: InputAction.Up,
  KeyW: InputAction.Up,
  ArrowDown: InputAction.Down,
  KeyS: InputAction.Down,
  ArrowLeft: InputAction.Left,
  KeyA: InputAction.Left,
  ArrowRight: InputAction.Right,
  KeyD: InputAction.Right,
  KeyJ: InputAction.Fire,
  Space: InputAction.Fire,
  KeyK: InputAction.AimLock,
  KeyL: InputAction.Formation,
  KeyI: InputAction.NodeDirectionLeft,
  KeyO: InputAction.NodeDirectionRight,
  KeyU: InputAction.Device,
  Escape: InputAction.Pause,
  Enter: InputAction.Confirm,
  Backspace: InputAction.Cancel,
});

function isMoveAction(action: InputAction): boolean {
  return action === InputAction.Up || action === InputAction.Down || action === InputAction.Left || action === InputAction.Right;
}

export class InputManager {
  readonly held = new Uint8Array(INPUT_ACTION_COUNT);
  readonly pressed = new Uint8Array(INPUT_ACTION_COUNT);
  readonly released = new Uint8Array(INPUT_ACTION_COUNT);
  private readonly rawHeld = new Uint8Array(INPUT_ACTION_COUNT);
  private readonly pendingPressed = new Uint8Array(INPUT_ACTION_COUNT);
  private readonly pendingReleased = new Uint8Array(INPUT_ACTION_COUNT);
  private readonly gamepadHeld = new Uint8Array(INPUT_ACTION_COUNT);
  private readonly previousGamepadHeld = new Uint8Array(INPUT_ACTION_COUNT);
  private target: Window | null = null;
  moveX = 0;
  moveY = 0;
  touchMoveX = 0;
  touchMoveY = 0;
  touchFire = false;
  pointerFire = false;
  pointerX = 0;
  pointerY = 0;
  /** ポインタ追従が移動ソースを持っているか。移動系の他デバイス入力で剥奪される。 */
  pointerMoveActive = false;
  lastInputKind: 'keyboard' | 'gamepad' | 'pointer' | 'touch' = 'keyboard';
  suppressMouseUntil = 0;
  private gamepadConnected = false;
  private nextGamepadScanAt = 0;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (isNativeUiControl(event.target)) return;
    const action = KEY_ACTION[event.code];
    if (action === undefined) return;
    event.preventDefault();
    if (this.rawHeld[action] === 0) this.pendingPressed[action] = 1;
    this.rawHeld[action] = 1;
    this.lastInputKind = 'keyboard';
    // 移動キーだけが移動ソースを奪う（射撃キーはマウス移動と併用できる）。
    if (isMoveAction(action)) this.pointerMoveActive = false;
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (isNativeUiControl(event.target)) return;
    const action = KEY_ACTION[event.code];
    if (action === undefined) return;
    event.preventDefault();
    if (this.rawHeld[action] !== 0) this.pendingReleased[action] = 1;
    this.rawHeld[action] = 0;
    this.lastInputKind = 'keyboard';
  };

  private readonly onBlur = (): void => {
    this.rawHeld.fill(0);
    this.gamepadHeld.fill(0);
    this.touchFire = false;
    this.pointerFire = false;
    this.pointerMoveActive = false;
    this.touchMoveX = 0;
    this.touchMoveY = 0;
  };

  attach(target: Window): void {
    if (this.target === target) return;
    this.dispose();
    this.target = target;
    target.addEventListener('keydown', this.onKeyDown, { passive: false });
    target.addEventListener('keyup', this.onKeyUp, { passive: false });
    target.addEventListener('blur', this.onBlur);
  }

  beginTick(nowMs: number): void {
    // HOTPATH-BEGIN
    this.pollGamepad(nowMs);
    for (let i = 0; i < INPUT_ACTION_COUNT; i += 1) {
      let nextHeld = this.rawHeld[i] !== 0 || this.gamepadHeld[i] !== 0 ? 1 : 0;
      if (i === InputAction.Fire && (this.touchFire || this.pointerFire)) nextHeld = 1;
      const priorHeld = this.held[i];
      const transientTap = this.pendingPressed[i] !== 0 && this.pendingReleased[i] !== 0 && nextHeld === 0 && priorHeld === 0;
      this.pressed[i] = (nextHeld !== 0 && priorHeld === 0) || transientTap ? 1 : 0;
      this.released[i] = (nextHeld === 0 && priorHeld !== 0) || transientTap ? 1 : 0;
      this.held[i] = nextHeld;
      this.pendingPressed[i] = 0;
      this.pendingReleased[i] = 0;
    }
    let x = (this.held[InputAction.Right] !== 0 ? 1 : 0) - (this.held[InputAction.Left] !== 0 ? 1 : 0);
    let y = (this.held[InputAction.Down] !== 0 ? 1 : 0) - (this.held[InputAction.Up] !== 0 ? 1 : 0);
    if (Math.abs(this.touchMoveX) > Math.abs(x)) x = this.touchMoveX;
    if (Math.abs(this.touchMoveY) > Math.abs(y)) y = this.touchMoveY;
    const lenSq = x * x + y * y;
    if (lenSq > 1) {
      const inv = 1 / Math.sqrt(lenSq);
      x *= inv;
      y *= inv;
    }
    this.moveX = x;
    this.moveY = y;
    // HOTPATH-END
  }

  setTouchMove(x: number, y: number): void {
    this.touchMoveX = clamp(x, -1, 1);
    this.touchMoveY = clamp(y, -1, 1);
    this.pointerMoveActive = false;
    this.noteDirectTouch();
  }

  clearTouchMove(directTouchEnd = false): void {
    this.touchMoveX = 0;
    this.touchMoveY = 0;
    if (directTouchEnd) this.noteDirectTouch();
  }

  setTouchFire(active: boolean): void {
    // TouchControls resets to inactive on hide; only a real state change may
    // claim the touch input kind, or pausing on keyboard relabels the device.
    if (this.touchFire === active) return;
    this.touchFire = active;
    this.noteDirectTouch();
  }

  /**
   * プレイ中に押し始めたキーをメニューへ持ち越さない。
   * DOM 側の native button へ focus が移ると keyup を InputManager が受け取らないため、
   * モード境界でキーボード由来の保持だけを明示的に解放する。
   */
  releaseKeyboardAction(action: InputAction): void {
    if (this.rawHeld[action] !== 0 || this.held[action] !== 0) this.pendingReleased[action] = 1;
    this.rawHeld[action] = 0;
    this.pendingPressed[action] = 0;
  }

  pulseTouchAction(action: InputAction): void {
    this.pendingPressed[action] = 1;
    this.pendingReleased[action] = 1;
    this.noteDirectTouch();
  }

  pulsePointerAction(action: InputAction): void {
    if (!this.notePointer()) return;
    this.pendingPressed[action] = 1;
    this.pendingReleased[action] = 1;
  }

  setPointerFire(active: boolean): void {
    if (active) {
      if (!this.notePointer()) return;
      this.pointerFire = true;
      return;
    }
    if (!this.pointerFire) return;
    this.pointerFire = false;
    if (performance.now() >= this.suppressMouseUntil) this.lastInputKind = 'pointer';
  }

  /** ポインタ位置（sim 座標）を追従目標にする。座標変換とクランプは入力層の責務。 */
  setPointerTarget(x: number, y: number): void {
    if (!this.notePointer()) return;
    this.pointerX = x;
    this.pointerY = y;
    this.pointerMoveActive = true;
  }

  notePointer(): boolean {
    const now = performance.now();
    if (now < this.suppressMouseUntil) return false;
    this.lastInputKind = 'pointer';
    return true;
  }

  noteDirectTouch(): void {
    this.lastInputKind = 'touch';
    this.suppressMouseUntil = performance.now() + 850;
  }

  private pollGamepad(nowMs: number): void {
    // navigator.getGamepads() crosses the browser/device boundary. On phones
    // without a controller, probing it 60 times per second wastes main-thread
    // time while a finger is already driving the touch controller.
    if (!this.gamepadConnected && nowMs < this.nextGamepadScanAt) return;
    const getGamepads = navigator.getGamepads;
    if (typeof getGamepads !== 'function') {
      this.gamepadHeld.fill(0);
      this.gamepadConnected = false;
      this.nextGamepadScanAt = nowMs + 1000;
      return;
    }
    const pads = getGamepads.call(navigator);
    const pad = pads[0];
    if (!pad) {
      this.gamepadHeld.fill(0);
      this.gamepadConnected = false;
      this.nextGamepadScanAt = nowMs + 500;
      return;
    }
    this.gamepadConnected = true;
    this.previousGamepadHeld.set(this.gamepadHeld);
    this.gamepadHeld.fill(0);
    const x = Math.abs(pad.axes[0] ?? 0) > 0.2 ? (pad.axes[0] ?? 0) : 0;
    const y = Math.abs(pad.axes[1] ?? 0) > 0.2 ? (pad.axes[1] ?? 0) : 0;
    if (x < -0.2 || pad.buttons[14]?.pressed) this.gamepadHeld[InputAction.Left] = 1;
    if (x > 0.2 || pad.buttons[15]?.pressed) this.gamepadHeld[InputAction.Right] = 1;
    if (y < -0.2 || pad.buttons[12]?.pressed) this.gamepadHeld[InputAction.Up] = 1;
    if (y > 0.2 || pad.buttons[13]?.pressed) this.gamepadHeld[InputAction.Down] = 1;
    // 軸・dpad どちらの方向入力でも移動ソースはパッド側へ移る。
    if (
      this.gamepadHeld[InputAction.Left] !== 0
      || this.gamepadHeld[InputAction.Right] !== 0
      || this.gamepadHeld[InputAction.Up] !== 0
      || this.gamepadHeld[InputAction.Down] !== 0
    ) {
      this.pointerMoveActive = false;
    }
    if (pad.buttons[0]?.pressed || (pad.buttons[7]?.value ?? 0) > 0.25) this.gamepadHeld[InputAction.Fire] = 1;
    if (pad.buttons[4]?.pressed) this.gamepadHeld[InputAction.AimLock] = 1;
    if (pad.buttons[1]?.pressed) this.gamepadHeld[InputAction.Formation] = 1;
    if (pad.buttons[5]?.pressed) this.gamepadHeld[InputAction.NodeDirectionRight] = 1;
    if (pad.buttons[2]?.pressed) this.gamepadHeld[InputAction.Device] = 1;
    if (pad.buttons[9]?.pressed) this.gamepadHeld[InputAction.Pause] = 1;
    if (pad.buttons[0]?.pressed) this.gamepadHeld[InputAction.Confirm] = 1;
    for (let i = 0; i < INPUT_ACTION_COUNT; i += 1) {
      if (this.gamepadHeld[i] !== 0 && this.previousGamepadHeld[i] === 0) this.pendingPressed[i] = 1;
      if (this.gamepadHeld[i] === 0 && this.previousGamepadHeld[i] !== 0) this.pendingReleased[i] = 1;
    }
    if (x !== 0 || y !== 0) {
      this.lastInputKind = 'gamepad';
    }
  }

  dispose(): void {
    if (!this.target) return;
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('blur', this.onBlur);
    this.target = null;
  }
}
